import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Children, useEffect, isValidElement, cloneElement, memo } from 'react';
import { offset } from '@floating-ui/core';
import { useFloating, useInteractions, useClick, useDismiss, useHover, flip, autoUpdate } from '@floating-ui/react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { Button } from '../../Controls/Button';
import Icons from '../../Elements/Icons';
import { DropdownItem } from './components';
import { ANIMATION_DURATION_MS, DEFAULT_Z_INDEX } from './constants';
import { DropdownContextProvider, useDropdownContext } from './context';
import { getPlacement, stopPropagation } from './helpers';
import { ButtonWrapper, InnerWrapper, Wrapper } from './styled';
const isCustomButton = (props) => 'renderButton' in props && typeof props.renderButton === 'function';
const DropdownListComponent = (props) => {
    const { children, align = 'left', zIndex = DEFAULT_Z_INDEX, minWidth = true, onVisibilityChange, isSubmenu = false, fixedParent, dataTestId, disabled = false, isLoading = false, } = props;
    const isChildrenArrayEmpty = Array.isArray(children) ? children.every((c) => !!c === false) : false;
    const { isShown, update } = useDropdownContext();
    const { refs, floatingStyles, context, placement } = useFloating({
        open: isShown,
        placement: getPlacement(align, isSubmenu),
        onOpenChange: (open) => {
            update(open);
        },
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(1),
            flip({
                fallbackPlacements: isSubmenu
                    ? ['left', 'right-start', 'left-start', 'right-end', 'left-end']
                    : ['bottom-end', 'bottom-start', 'top-end', 'top-start'],
            }),
        ],
        strategy: fixedParent ? 'fixed' : 'absolute',
    });
    const isTop = placement.startsWith('top');
    const click = useClick(context, {
        enabled: !isSubmenu && !disabled && !isLoading,
    });
    const hover = useHover(context, {
        enabled: isSubmenu && !disabled && !isLoading,
        delay: { open: 150, close: 100 },
    });
    const dismiss = useDismiss(context, {
        enabled: isShown,
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, dismiss]);
    useEffect(() => {
        onVisibilityChange?.(isShown);
    }, [isShown, onVisibilityChange]);
    if (isChildrenArrayEmpty) {
        return null;
    }
    let trigger;
    if (isCustomButton(props)) {
        const rendered = props.renderButton(isShown);
        if (isValidElement(rendered)) {
            const baseProps = {
                ...getReferenceProps(),
                ref: refs.setReference,
                disabled: disabled || rendered.props.disabled,
                isLoading: isLoading || rendered.props.isLoading,
                ...(rendered.props.dataTestId ? {} : { dataTestId }),
            };
            if (props.isComboButton) {
                const chevron = _jsx(Icons.Chevron, { align: isShown ? 'top' : 'bottom', size: 20 }, '__chevron');
                trigger = cloneElement(rendered, baseProps, ...Children.toArray(rendered.props.children), chevron);
            }
            else {
                trigger = cloneElement(rendered, baseProps);
            }
        }
        else {
            trigger = null;
        }
    }
    else {
        const variant = props.variant ?? 'secondary';
        trigger = (_jsx(Button, { ...getReferenceProps(), id: props.id, className: props.className, disabled: disabled, isLoading: isLoading, [variant]: true, iconOnly: props.iconOnly ?? true, ref: refs.setReference, dataTestId: dataTestId, children: _jsx(Icons.More, { size: 20, active: isShown }) }));
    }
    return (_jsxs(_Fragment, { children: [createPortal(_jsx(CSSTransition, { in: isShown, timeout: ANIMATION_DURATION_MS, transitionAppear: true, unmountOnExit: true, children: _jsx(Wrapper, { ...getFloatingProps(), style: floatingStyles, $zIndex: zIndex, $isTop: isTop, $visible: isShown, ref: refs.setFloating, "data-testid": 'dropdown-wrapper', children: _jsx(InnerWrapper, { $minWidth: minWidth, $zIndex: zIndex, "data-testid": 'dropdown-inner-wrapper', children: children }) }) }), document.body), _jsx(ButtonWrapper, { onClick: stopPropagation, children: trigger })] }));
};
const DropdownListWrapper = memo(function DropdownListWrapper(props) {
    return (_jsx(DropdownContextProvider, { children: _jsx(DropdownListComponent, { ...props }) }));
});
export const DropdownList = Object.assign(DropdownListWrapper, {
    DropdownItem,
});
